import React from "react";
import { Outlet, Navigate } from "react-router-dom";
import { ServiceProvider } from "../Context/ServiceProvider";
import { SERVICES } from "../Context/serviceConstants";
import AgmDocumentsPage from "../Services/MyFPO/components/AGMandBoardMeetings/AgmDocumentsPage";
import RulesActsPage from "../Services/MyFPO/components/AGMandBoardMeetings/RulesActs";
import {
  FaFileInvoice,
  FaChartLine,
  FaUsers,
  FaClipboardCheck,
  FaFolderOpen,
  FaHandshake,
  FaMoneyCheckAlt,
  FaFileAlt,
  FaNetworkWired,
  FaShieldAlt,
  FaTachometerAlt,
  FaSeedling,
  FaGlobeAsia,
  FaBullhorn,
  FaAddressBook,
  FaLandmark,
  FaFileUpload,
  FaReceipt,
  FaRupeeSign
} from "react-icons/fa";

const Register = React.lazy(() => import("../Components/Register"));
// Public pages
const Home = React.lazy(() => import("../Components/Home"));
const LoginPage = React.lazy(() => import("../Components/LoginPage"));
const ForgotPassword = React.lazy(() => import("../Components/ForgotPassword"));
const SsoWebsiteLogin = React.lazy(() => import("../Components/SsoWebsiteLogin"));
const SsoWebsiteLogout = React.lazy(() => import("../Components/SsoWebsiteLogout"));
const HomeGateway = React.lazy(() => import("../Components/HomeGateway"));
const SubscriptionPage = React.lazy(() => import("../Components/SubscriptionPage"));
const ProfilePage = React.lazy(() => import("../Components/Profile/ProfilePage"));
const ComingSoonPage = React.lazy(() => import("../Components/ComingSoonPage"));
const ServiceAccessSummary = React.lazy(() => import("../Components/ServiceAccessSummary"));
const ServicesPage = React.lazy(() => import("../Components/ServicesPage"));

// -------------------- MyFPO main pages --------------------
const MyFpoDashboard = React.lazy(() => import("../Services/MyFPO/Dashboard/MyFpoDashboard"));
const AboutFPOPage = React.lazy(() => import("../Services/MyFPO/pages/AboutFPO/AboutFPOPage"));
const BusinessPlanPage = React.lazy(() => import("../Services/MyFPO/pages/BusinessPlan/BusinessPlan"));
// Shared across myFpo/mybusiness (KrishiCoinsWidget.jsx is service-agnostic) -- not under
// Services/ like the pages above, so it imports from ../shared instead.
const KrishiCoinsPage = React.lazy(() => import("../shared/dashboard/KrishiCoinsPage"));
const CapacityBuildingPage = React.lazy(() => import("../Services/MyFPO/pages/CapacityBuilding/CapacityBuilding"));
const AgmBoardPage = React.lazy(() => import("../Services/MyFPO/pages/AGMandBoardMeetings/AgmandBoarding"));
const LoansSchemasPage = React.lazy(() => import("../Services/MyFPO/pages/LoansandSchemes/LoansandSchemas"));
// Real Coming Soon destinations for My Directory/My Trade — SIDENAV_REDESIGN_PLAN_V2.md — these
// previously resolved to a bare `path:""` + `#hash` anchor into the Profile page instead of a
// real navigable page, same shared-across-both-services pattern as LoansSchemasPage above.
const MyDirectoryPage = React.lazy(() => import("../Services/MyFPO/pages/MyDirectory/MyDirectoryPage"));
const MyTradePage = React.lazy(() => import("../Services/MyFPO/pages/MyTrade/MyTradePage"));
const ReportsPage = React.lazy(() => import("../Services/MyFPO/pages/Reports/Reports"));
const FPCConnectPage = React.lazy(() => import("../Services/MyFPO/pages/FPCConnect/FpcConnect"));

// -------------------- Accounts submodules --------------------
const FpoAccountHomePage = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/FpoAccountHomePage"));
const FpcDataView = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Fpc/FpcDataView"));
const FpcRegister = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Fpc/FpcPost"));
const FpcUpdate = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Fpc/FpcUpdate"));
const FpoSetupPage = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Fpc/FpoSetupPage"));

const VendorDataView = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Vendor/VendorDataView"));
const VendorRegister = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Vendor/VendorPost"));
const VendorUpdate = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Vendor/VendorUpdate"));

const ProductDataView = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Product/ProductDataView"));
const ProductRegister = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Product/ProductPost"));
const ProductUpdate = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Product/ProductUpdate"));
const FpoOwnProductRegister = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Product/FpoOwnProductPost"));
const FpoOwnProductView = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Product/FpoOwnProductView"));
const ShareCapitalDataView = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/ShareCapital/ShareCapitalDataView"));
const ShareCapitalRegister = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/ShareCapital/ShareCapitalPost"));
const ShareCapitalUpdate = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/ShareCapital/ShareCapitalUpdate"));

const PurchaseDataView = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Purchase/PurchaseDataView"));
const PurchaseRegister = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Purchase/PurchasePost"));
const PurchaseOrderUpdate = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Purchase/PurchaseOrderUpdate"));
const PurchaseInvoiceUpdate = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Purchase/PurchaseInvoiceUpdate"));
const PurchaseReturnUpdate = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Purchase/PurchaseReturnUpdate"));
const PurchaseUpdateWrapper = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Purchase/PurchaseUpdateWrapper"));

const SalesDataView = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Sales/SalesDataView"));
const SalesRegister = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Sales/SalesPost"));
// SalesUpdate.jsx does not exist (only SalesUpdateWrapper.jsx below) and this const was never
// referenced outside commented-out JSX -- dropped rather than pointed at a real file.
const SalesQuotationUpdate = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Sales/SalesQuotationUpdate"));
const SalesInvoiceUpdate = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Sales/SalesInvoiceUpdate"));
const SalesChallanUpdate = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Sales/DeliveryChallanUpdate"));
const SalesReturnUpdate = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Sales/SalesReturnUpdate"));
const SalesUpdateWrapper = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Sales/SalesUpdateWrapper"));

// Submodules with only view pages
const StockDataView = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Stock/StockDataView"));
const BankDataView = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Bank/BankDataView"));
const BankRegister = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Bank/BankPost"));
const BankUpdate = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Bank/BankUpdate"));
const CashBookDataView = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/CashBook/CashBookDataView"));
const CashBookRegister = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/CashBook/CashBookPost"));
const CashBookUpdate = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/CashBook/CashBookUpdate"));
const LedgerDataView = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Ledger/LedgerDataView"));
const ReportsDataView = React.lazy(() => import("../Services/MyFPO/pages/FPOAccounts/Reports/ReportsDataView"));

// Main compliance pages
const Compliances = React.lazy(() => import("../Services/MyFPO/pages/Compliances/Compliances"));
const StatutoryCompliances = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/StatutoryCompliances"));
const LegalCompliances = React.lazy(() => import("../Services/MyFPO/pages/Compliances/LegalCompliances/LegalCompliances"));

const AnnualCompliances = React.lazy(() => import("../Services/MyFPO/pages/Compliances/AnnualCompliances/AnnualCompliances"));

// Statutory Compliances Forms
const FpoFpcRegistration = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/FpoFpcRegistration"));
const MsmeRegistration = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/MsmeRegistration"));
const ShopsEstablishmentRegistration = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/ShopsEstablishmentRegistration"));
const CompanyRegistration = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/CompanyRegistration"));
const PanRegistration = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/PanRegistration"));
const TradeLicenseRegistration = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/TradeLicenseRegistration"));
const GstRegistration = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/GstRegistration"));
const FssaiRegistration = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/FssaiRegistration"));
const LabourLicenseRegistration = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/LabourLicenseRegistration"));
const IecCodeRegistration = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/IecCodeRegistration"));
const IsoRegistration = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/IsoRegistration"));
const FpoFpcRegistrationDataView = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/views/FpoFpcRegistrationDataView"));
const FpoFpcRegistrationUpdate = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/FpoFpcRegistrationUpdate"));
const MsmeRegistrationDataView = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/views/MsmeRegistrationDataView"));
const MsmeRegistrationUpdate = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/MsmeRegistrationUpdate"));
const ShopsEstablishmentRegistrationDataView = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/views/ShopsEstablishmentRegistrationDataView"));
const ShopsEstablishmentRegistrationUpdate = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/ShopsEstablishmentRegistrationUpdate"));
const CompanyRegistrationDataView = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/views/CompanyRegistrationDataView"));
const CompanyRegistrationUpdate = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/CompanyRegistrationUpdate"));
const PanRegistrationDataView = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/views/PanRegistrationDataView"));
const PanRegistrationUpdate = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/PanRegistrationUpdate"));
const TradeLicenseRegistrationDataView = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/views/TradeLicenseRegistrationDataView"));
const TradeLicenseRegistrationUpdate = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/TradeLicenseRegistrationUpdate"));
const GstRegistrationDataView = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/views/GstRegistrationDataView"));
const GstRegistrationUpdate = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/GstRegistrationUpdate"));
const FssaiRegistrationDataView = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/views/FssaiRegistrationDataView"));
const FssaiRegistrationUpdate = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/FssaiRegistrationUpdate"));
const LabourLicenseRegistrationDataView = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/views/LabourLicenseRegistrationDataView"));
const LabourLicenseRegistrationUpdate = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/LabourLicenseRegistrationUpdate"));
const IecCodeRegistrationDataView = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/views/IecCodeRegistrationDataView"));
const IecCodeRegistrationUpdate = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/IecCodeRegistrationUpdate"));
const IsoRegistrationDataView = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/views/IsoRegistrationDataView"));
const IsoRegistrationUpdate = React.lazy(() => import("../Services/MyFPO/pages/Compliances/StatutoryCompliances/forms/IsoRegistrationUpdate"));


// -------------------- MyBusiness main pages --------------------
const MyBusinessDashboard = React.lazy(() => import("../Services/MyBusiness/Dashboard/MyBusinessDashboard"));
const AboutBusinessPage = React.lazy(() => import("../Services/MyBusiness/Home/AboutBusinessPage"));
const CompanySetupPage = React.lazy(() => import("../Services/MyBusiness/Company/CompanySetupPage"));
const CompanyDataView = React.lazy(() => import("../Services/MyBusiness/Company/CompanyDataView"));
const CompanyRegister = React.lazy(() => import("../Services/MyBusiness/Company/CompanyRegister"));
const CompanyUpdate = React.lazy(() => import("../Services/MyBusiness/Company/CompanyUpdate"));
const MemberDataView = React.lazy(() => import("../Services/MyBusiness/Membership/MemberDataView"));
const MemberPost = React.lazy(() => import("../Services/MyBusiness/Membership/MemberPost"));
const MemberUpdate = React.lazy(() => import("../Services/MyBusiness/Membership/MemberUpdate"));

// -------------------- Public / other placeholders --------------------
const PlaceholderPage = ({ title }) => <div>{title} Page</div>;
const AdminUsersDataTable = React.lazy(() =>
  import("../SuperAdminUsers/Pages/AdminUsersDataTable")
);
const AdminUsersPost = React.lazy(() =>
  import("../SuperAdminUsers/Pages/AdminUsersPost")
);
const AdminUsersUpdate = React.lazy(() =>
  import("../SuperAdminUsers/Pages/AdminUsersUpdate")
);
const AnonymoususerUpdate = React.lazy(() =>
  import("../SuperAdminUsers/Pages/AnonymousUserUpdate")
);
const SubscriptionPlanPage = React.lazy(() =>
  import("../Services/Admin/pages/SubscriptionPlanPage")
);
const YuvaKrushiRegistrationsPage = React.lazy(() =>
  import("../Services/Admin/pages/YuvaKrushiRegistrationsPage")
);
const DirectorySuggestionsPage = React.lazy(() =>
  import("../Services/Admin/pages/DirectorySuggestionsPage")
);
const GovernmentProgressReportPage = React.lazy(() =>
  import("../Services/Government/pages/GovernmentProgressReportPage")
);
const MyPaymentsPage = React.lazy(() => import("../Components/MyPaymentsPage"));
const PaymentsAdminPage = React.lazy(() =>
  import("../Services/Admin/pages/PaymentsAdminPage")
);
const PaymentSettingsPage = React.lazy(() =>
  import("../Services/Admin/pages/PaymentSettingsPage")
);
const ProgressReportDataPage = React.lazy(() =>
  import("../Services/Admin/pages/ProgressReportDataPage")
);
const GovernmentOfficersPage = React.lazy(() =>
  import("../Services/Admin/pages/GovernmentOfficersPage")
);
const SafeMissionRegistrationsPage = React.lazy(() =>
  import("../Services/Admin/pages/SafeMissionRegistrationsPage")
);
const NewsAlertsAdminPage = React.lazy(() =>
  import("../Services/Admin/pages/NewsAlertsAdminPage")
);
const ErsAssessmentsPage = React.lazy(() =>
  import("../Services/Admin/pages/ErsAssessmentsPage")
);
const AgmDocumentsAdminPage = React.lazy(() =>
  import("../Services/Admin/pages/AgmDocumentsAdminPage")
);
const ServiceSubscriptionConfigPage = React.lazy(() =>
  import("../Services/Admin/pages/ServiceSubscriptionConfigPage")
);
const AccessibilityRulePage = React.lazy(() =>
  import("../Services/Admin/pages/AccessibilityRulePage")
);
const RolePage = React.lazy(() =>
  import("../Services/Admin/pages/RolePage")
);

export const publicRoutes = [
  { path: "/", element: <LoginPage /> },
  { path: "/register", element: <Register /> },
  { path: "/forgot-password", element: <ForgotPassword /> },
  // Signed in on the FEED WORLD website → this application without a second login
  { path: "/sso/website", element: <SsoWebsiteLogin /> },
  // Logged out on the FEED WORLD website → logged out here too, then back to the website
  { path: "/sso/logout", element: <SsoWebsiteLogout /> },
  {
    path: "/home",
    element: <Home />,
    children: [
      { index: true, element: <HomeGateway /> },
      { path: "subscription", element: <SubscriptionPage /> },
      { path: "profile", label: "My Profile", element: <ProfilePage /> },
      { path: "payments", label: "My Payments", element: <MyPaymentsPage /> },
      { path: "coming-soon", element: <ComingSoonPage /> },
      { path: "service-summary", element: <ServiceAccessSummary /> }
    ]
  },
  {
    // SIDENAV_REDESIGN_PLAN.md Part 2 — the Global SideNav's "Services" destination. Reuses
    // Home.jsx as its layout (same self-guarded-via-useAuth() pattern as /home, not wrapped in
    // ProtectedRoutes) purely so the Header + Global SideNav shell renders around it too.
    path: "/services",
    element: <Home />,
    children: [{ index: true, element: <ServicesPage /> }],
  },
  // Government officers' landing page (and System Administrators'): hands off to the website's Government dashboard; guards itself like /home
  { path: "/progress-report", element: <GovernmentProgressReportPage /> },
  { path: "/unauthorized", element: <div>Access Denied</div> },
];

// -------------------- PRIVATE ROUTES --------------------
export const privateRoutes = [
  {
    path: "/myFpo",
    element: (
      <ServiceProvider service={SERVICES.MY_FPO}>
        <Outlet />
      </ServiceProvider>
    ),
    serviceKey: "myFpo",
    label: "Org Services",
    img: "assets/img/category/cooperativ.webp",
    allowedRoles: [],

    // fullUserData: SIDENAV_REDESIGN_PLAN.md Part 1, decision #3 — "My Account" stays flat for
    // ANY account with no personal fpoEntity/membershipEntity to scope Purchase/Sales/Stock/etc.
    // against (covers systemAdmin and department staff today, and any future role/userType in
    // the same situation) rather than checking a specific role id.
    getModules: (userRoles, hasEntityAccess, fullUserData) => {
      const hasPersonalEntity = !!(fullUserData?.fpoEntity || fullUserData?.membershipEntity);

      // SIDENAV_REDESIGN_PLAN_V2.md §6 — !hasPersonalEntity must branch on internalUser: internal
      // accounts (systemAdmin/department staff) never have a personal entity by design and never
      // will, so their flat My Account row points at FPC Management directly; an external account
      // with no entity yet is a separate, temporary case (mid-onboarding) not handled here — see
      // §6's Entity Setup note (that redirect gate is a separate ProtectedRoutes-level change, not
      // yet built).
      const isInternalUser = fullUserData?.userTypeEntity?.internalUser === true;

      const allModules = [
        {
          path: "",
          label: "Dashboard",
          icon: <FaTachometerAlt />,
          category: "overview",
          allowedRoles: [],
        },
        // SIDENAV_REDESIGN_PLAN_V2.md §1 gap 7 — restored as its own top-level section, reversing
        // v1 decision #1 (which had removed it from the sidebar entirely). No permission gate:
        // being inside /myFpo at all already proves the service exists for this account.
        { path: "about", label: "About Org", icon: <FaTachometerAlt />, category: "about", allowedRoles: [] },
        ...(isInternalUser
          // SIDENAV_REDESIGN_PLAN_V2.md §6 — revised again: internal accounts get the same
          // "Accounts" dropdown component as everyone else (not a separate flat-link special
          // case), just with a `children` array containing only FPC Management under Registers —
          // Members/Transactions have nothing to show them (no personal share capital/cashbook),
          // so those sub-groups simply don't exist here rather than rendering empty.
          ? [{
              path: "accounts",
              label: "My Account",
              icon: <FaFileInvoice />,
              category: "finance",
              requiredPermission: { entityKey: "fpo", action: "R" },
              children: [
                { path: "accounts/fpc", label: "FPC Management", subGroup: "Registers", requiredPermission: { entityKey: "fpo", action: "R" } },
                { path: "accounts/products", label: "Product Management", subGroup: "Registers", requiredPermission: { entityKey: "products", action: "R" } },
              ],
            }]
          : !hasPersonalEntity
          ? [{
              path: "accounts",
              label: "My Account",
              icon: <FaFileInvoice />,
              category: "finance",
              requiredPermission: { entityKey: "fpo", action: "R" },
            }]
          : [{
              path: "accounts",
              label: "My Account",
              icon: <FaFileInvoice />,
              category: "finance",
              requiredPermission: { entityKey: "fpo", action: "R" },
              children: [
                // SIDENAV_REDESIGN_PLAN_V2.md §4 — Registers/Members/Transactions are
                // presentation-only sub-groups (subGroup, no permission of their own) rendered as
                // always-expanded labels inside this one dropdown by SideNav.jsx.
                { path: "accounts/vendor", label: "Vendor Management", subGroup: "Registers", requiredPermission: { entityKey: "vendor", action: "R" } },
                { path: "accounts/products", label: "Product Management", subGroup: "Registers", requiredPermission: { entityKey: "products", action: "R" } },
                { path: "accounts/purchase", label: "Purchase", subGroup: "Registers", requiredPermission: { entityKey: "purchaseOrder", action: "R" } },
                { path: "accounts/sales", label: "Sales", subGroup: "Registers", requiredPermission: { entityKey: "salesOrder", action: "R" } },
                { path: "accounts/stock", label: "Stock", subGroup: "Registers", requiredPermission: { entityKey: "stock", action: "R" } },
                // Members/Share Transfer point at the same shared page, disambiguated by #hash —
                // no separate "Share Capital" row (that's per-member data, reached via getById
                // drill-in from a Members row, not a nav destination — §1 gap 2).
                { path: "accounts/share-capital", hash: "members", label: "Members", subGroup: "Members", requiredPermission: { entityKey: "shareCapital", action: "R" } },
                { path: "accounts/share-capital", hash: "share-transfer", label: "Share Transfer", subGroup: "Members", requiredPermission: { entityKey: "shareTransfer", action: "R" } },
                { path: "accounts/cashbook", label: "Cash", subGroup: "Transactions", requiredPermission: { entityKey: "cashbook", action: "R" } },
                { path: "accounts/bank", label: "Bank", subGroup: "Transactions", requiredPermission: { entityKey: "bankRegistry", action: "R" } },
                { path: "accounts/ledger", label: "Ledger", subGroup: "Transactions", requiredPermission: { entityKey: "ledger", action: "R" } },
                { path: "accounts/reports", label: "Reports", subGroup: "Transactions", requiredPermission: { entityKey: "fpoReport", action: "R" } },
              ],
            }]),
        // No dedicated backend permission entity exists for these today — fall back to the same
        // coarse "fpo" entity check the "My Account"/"Compliances" parents use, rather than a
        // role-name list (see SIDENAV_REDESIGN_PLAN.md Part 1's "New Module-List Shape" note).
        { path: "loans-schemas", label: "Loans & Schemes", icon: <FaMoneyCheckAlt />, category: "finance", requiredPermission: { entityKey: "fpo", action: "R" }, websitePath: "/know-your-schemes" }, // Know Your Schemes on the FEED WORLD website (new tab, signed in)

        // Real Coming Soon pages now, not a `path:""` + `#hash` anchor back into the Profile page
        // — clicking these navigates to a dedicated page, same pattern as Loans & Schemes above.
        { path: "directory", label: "My Directory", icon: <FaUsers />, category: "directory", requiredPermission: { entityKey: "fpo", action: "R" } },
        { path: "trade", label: "My Trade", icon: <FaNetworkWired />, category: "trade", requiredPermission: { entityKey: "fpo", action: "R" } },

        {
          path: "compliances", label: "Compliances", icon: <FaClipboardCheck />, category: "governance",
          requiredPermission: { entityKey: "fpo", action: "R" },
          children: [
            { path: "compliances/statutory", label: "Statutory Compliances", requiredPermission: { entityKey: "fpo", action: "R" } },
            { path: "compliances/legal", label: "Legal Compliances", requiredPermission: { entityKey: "legalCompliance", action: "R" } },
            { path: "compliances/annual", label: "Annual Compliances", requiredPermission: { entityKey: "annualCompliance", action: "R" } },
          ],
        },
        { path: "agm-board", label: "Agm & Board", icon: <FaHandshake />, category: "governance", orgScoped: true, allowedRoles: ["fpoAdmin", "fpoMember"] },
        { path: "business-plan", label: "Business Plan", icon: <FaChartLine />, category: "growth", requiredPermission: { entityKey: "businessPlan", action: "R" } },
        { path: "capacity-building", label: "Capacity Building", icon: <FaUsers />, category: "growth", requiredPermission: { entityKey: "capacityBuilding", action: "R" } },
        {
          // ROLE_TO_PERMISSION_MIGRATION_PLAN.md: migrated to requiredPermission, reusing the
          // same "reports" entity as My Account > Reports (per explicit product decision).
          path: "reports",
          label: "Reports",
          icon: <FaFileAlt />,
          category: "growth",
          requiredPermission: { entityKey: "reports", action: "R" },
        },
        // {
        //   path: "fpc-connect",
        //   label: "FPC Connect",
        //   icon: <FaNetworkWired />,
        //   allowedRoles: ["systemAdmin", "fpoAdmin", "fpoMember", "fpoDepartment"],
        // },
      ];

      // Show every module (SideNav renders inaccessible ones disabled/grayed rather than
      // omitting them — 2026-08-20 UX decision), just annotate whether the user can enter it.
      // A module with `children` (My Account, Compliances, My Directory, My Trade) is unlocked
      // if it's own gate passes OR any real (path-bearing) child is unlocked — Coming-Soon
      // children (no `path`) never count either way, they're informational only.
      // `orgScoped` modules opt out of systemAdmin's blanket unlock: their data belongs to an
      // individual organization (AGM & Board), and internal staff belong to none -- the backend
      // rejects them for the same reason. Every other module keeps the bypass as before.
      const computeAccessible = (module) => {
        if (userRoles?.systemAdmin) return !module.orgScoped;
        if (module.requiredPermission) {
          return !!hasEntityAccess?.(module.requiredPermission.entityKey, module.requiredPermission.action);
        }
        if (module.allowedRoles) {
          return module.allowedRoles.length === 0 || module.allowedRoles.some((role) => userRoles[role]);
        }
        return true;
      };
      const annotate = (module) => {
        if (module.children) {
          const children = module.children.map(annotate);
          const anyChildUnlocked = children.some((c) => c.path && !c.locked);
          const ownAccessible = module.requiredPermission ? computeAccessible(module) : false;
          return { ...module, children, locked: !(anyChildUnlocked || ownAccessible) };
        }
        return { ...module, locked: !computeAccessible(module) };
      };
      return allModules.map(annotate);
    },

    children: [
      {
        index: true,
        element: <MyFpoDashboard />,
      },
      {
        path: "about",
        element: <AboutFPOPage />,
        allowedRoles: [],
      },
      {
        // B.5-style: allowedRoles swapped for the coarse "fpo" entity check, matching the
        // SideNav's own gate for this module — SIDENAV_REDESIGN_PLAN.md Part 1.
        path: "business-plan",
        element: <BusinessPlanPage />,
        allowedRoles: [],
        requiredPermission: { entityKey: "businessPlan", action: "R" },
      },
      {
        // No backend/permission concept, same as ERS — every account gets this page, matching
        // KrishiCoinsWidget.jsx's own Dashboard card (no locked/no-access state either).
        path: "krishi-coins",
        element: <KrishiCoinsPage />,
        allowedRoles: [],
      },
      {
        // PlanDetailsWidget.jsx's "Manage Plan" — same SubscriptionPage.jsx component already
        // registered at /home/subscription (self-contained: reads ?service= itself, no
        // dependency on being under Home.jsx specifically), registered here too so it renders
        // inside this service's own ServicesLayout/SideNav shell instead of jumping out to the
        // global Home one. No backend/permission concept, same as krishi-coins above.
        path: "subscription",
        element: <SubscriptionPage />,
        allowedRoles: [],
      },
      {
        // Mirrors /home/profile — same self-contained ProfilePage.jsx (reads AuthContext only,
        // no Home.jsx dependency), registered here too so the Service SideNav's persistent
        // "My Profile" row opens it inside this service's own ServicesLayout/SideNav shell
        // instead of jumping out to the global Home layout. No backend/permission concept.
        path: "profile",
        label: "My Profile",
        element: <ProfilePage />,
        allowedRoles: [],
      },
      {
        path: "capacity-building",
        element: <CapacityBuildingPage />,
        allowedRoles: [],
        requiredPermission: { entityKey: "capacityBuilding", action: "R" },
      },
      {
        path: "agm-board",
        // Org-scoped feature: internal platform roles (systemAdmin/fpoDepartment) are deliberately
        // excluded -- the documents belong to an individual organization, and internal staff
        // belong to none, so the backend rejects them too.
        allowedRoles: ["fpoAdmin", "fpoMember"],
        element: <AgmBoardPage />,
      },
      {
        path: "loans-schemas",
        element: <LoansSchemasPage />,
        allowedRoles: [],
        requiredPermission: { entityKey: "fpo", action: "R" },
      },
      {
        path: "directory",
        element: <MyDirectoryPage />,
        allowedRoles: [],
        requiredPermission: { entityKey: "fpo", action: "R" },
      },
      {
        path: "trade",
        element: <MyTradePage />,
        allowedRoles: [],
        requiredPermission: { entityKey: "fpo", action: "R" },
      },
      {
        path: "reports",
        element: <ReportsPage />,
        requiredPermission: { entityKey: "reports", action: "R" },
      },
      {
        // Service-mode's own "Services" link (SideNav.jsx) points here instead of the top-level
        // /services — same ServicesPage content, but rendered inside this service's own
        // ServicesLayout/serviceKey context, so the sidebar stays on this service's structure
        // instead of flipping to the Global one.
        path: "services",
        element: <ServicesPage />,
        allowedRoles: [],
      },
      {
        // B.5-style: same coarse "fpo" entity fallback as the other modules with no dedicated
        // permission entity (also currently delisted from the sidebar — see routes.js's
        // commented-out getModules() entry above).
        path: "fpc-connect",
        element: <FPCConnectPage />,
        allowedRoles: [],
        requiredPermission: { entityKey: "fpo", action: "R" },
      },

      // FPO entity setup - shown when fpoAdmin has no fpoEntity yet, AND (see
      // ProtectedRoutes.jsx's isEntitySetupPath exception) reachable by a user who doesn't hold
      // fpoAdmin yet at all - e.g. an Individual account redirected here for a cross-usertype
      // subscribe (Case 2). The role is only ever assigned at approval time, after this form is
      // submitted, so requiring it up front would make this page unreachable for exactly the
      // account it exists for.
      {
        path: "setup",
        element: <FpoSetupPage />,
        allowedRoles: [],
      },

      // Accounts with three-level nesting
      {
        path: "accounts",
        element: <Outlet />,
        allowedRoles: [], // B.5: cleared, requiredPermission on the index child is the sole gate
        children: [
          {
            index: true,
            element: <FpoAccountHomePage />,
            requiredPermission: { entityKey: "fpo", action: "R" },
          },
          // FPC Management — internal staff only (see AskUserQuestion 2026-08-24)
          {
            path: "fpc",
            element: <Outlet />,
            allowedRoles: [], // B.1-style migration: requiredPermission on every child is the sole gate
            requiresInternalUser: true,
            children: [
              {
                index: true,
                element: <FpcDataView />,
                requiredPermission: { entityKey: "fpo", action: "R" },
              },
              {
                path: "register",
                element: <FpcRegister />,
                label: "Register FPC",
                requiredPermission: { entityKey: "fpo", action: "C" },
              },
              {
                path: "view",
                element: <FpcUpdate />,
                requiredPermission: { entityKey: "fpo", action: "R" },
              },
              {
                path: "update",
                element: <FpcUpdate />,
                requiredPermission: { entityKey: "fpo", action: "E" },
              },
            ],
          },
          // Vendor Management
          {
            path: "vendor",
            element: <Outlet />,
            // B.5 (PERMISSIONS_IMPLEMENTATION_PLAN.md): allowedRoles cleared (not removed —
            // an empty array here overrides rather than falls back to the parent "accounts"
            // node's stricter list, per AppRoutes.jsx's `||` cascade). requiredPermission on
            // every child below is the sole gate now, live-verified.
            allowedRoles: [],
            children: [
              {
                index: true,
                element: <VendorDataView />,
                requiredPermission: { entityKey: "vendor", action: "R" },
              },
              {
                path: "register",
                element: <VendorRegister />,
                requiredPermission: { entityKey: "vendor", action: "C" },
              },
              {
                path: "update",
                element: <VendorUpdate />,
                requiredPermission: { entityKey: "vendor", action: "E" },
              },
            ],
          },
          // Product Management
          {
            path: "products",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <ProductDataView />,
                requiredPermission: { entityKey: "products", action: "R" },
              },
              {
                path: "register",
                element: <ProductRegister />,
                requiredPermission: { entityKey: "products", action: "C" },
              },
              {
                path: ":id",
                element: <ProductUpdate />,
                requiredPermission: { entityKey: "products", action: "E" },
              },
              {
                path: "addOwnProduct",
                element: <FpoOwnProductRegister />,
                requiredPermission: { entityKey: "products", action: "C" },
              },
              {
                path: "viewOwnProducts",
                element: <FpoOwnProductView />,
                requiredPermission: { entityKey: "products", action: "R" },
              },
            ],
          },
          // Share Capital Management
          {
            path: "share-capital",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <ShareCapitalDataView />,
                requiredPermission: { entityKey: "shareCapital", action: "R" },
              },
              {
                path: "register",
                element: <ShareCapitalRegister />,
                requiredPermission: { entityKey: "shareCapital", action: "C" },
              },
              {
                path: "update/:id/:type",
                element: <ShareCapitalUpdate />,
                // "E" relaxed to "R": the shareCapital type renders read-only for view-only
                // users (see ShareCapitalUpdate.jsx); member/shareTransfer still self-enforce
                // Edit via their own in-component canEditForType check.
                requiredPermission: { entityKey: "shareCapital", action: "R" },
              },
            ],
          },
          // Purchase Management
          {
            path: "purchase",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <PurchaseDataView />,
                requiredPermission: { entityKey: "purchaseOrder", action: "R" },
              },
              {
                path: "register",
                element: <PurchaseRegister />,
                requiredPermission: { entityKey: "purchaseOrder", action: "C" },
              },
              // {
              //   path: ":id",
              //   element: <PurchaseOrderUpdate />,
              // },
              // {
              //   path: "invoice/:id",
              //   element: <PurchaseInvoiceUpdate />,
              // },
              // {
              //   path: "debit/:id",
              //   element: <PurchaseReturnUpdate />,
              // },
              {
                path: "update",
                element: <PurchaseUpdateWrapper />,
                requiredPermission: { entityKey: "purchaseOrder", action: "E" },
              },
            ],
          },
          // Sales Management
          {
            path: "sales",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <SalesDataView />,
                requiredPermission: { entityKey: "salesOrder", action: "R" },
              },
              {
                path: "register",
                element: <SalesRegister />,
                requiredPermission: { entityKey: "salesOrder", action: "C" },
              },
              // {
              //   path: "Sales/Quotation/update/:id",
              //   element: <SalesQuotationUpdate />,
              // },
              // {
              //   path: "invoice/update/:id",
              //   element: <SalesInvoiceUpdate />,
              // },
              // {
              //   path: "challan/update/:id",
              //   element: <SalesChallanUpdate />,
              // },
              // {
              //   path: "salesReturn/update/:id",
              //   element: <SalesReturnUpdate />,
              // },
              {
                path: "update",
                element: <SalesUpdateWrapper />,
                requiredPermission: { entityKey: "salesOrder", action: "E" },
              },
            ],
          },
          {
            path: "stock",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <StockDataView />,
                requiredPermission: { entityKey: "stock", action: "R" },
              },
            ],
          },
          // Bank Management
          {
            path: "bank",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <BankDataView />,
                requiredPermission: { entityKey: "bankRegistry", action: "R" },
              },
              {
                path: "register",
                element: <BankRegister />,
                requiredPermission: { entityKey: "bankRegistry", action: "C" },
              },
              {
                path: "update",
                element: <BankUpdate />,
                requiredPermission: { entityKey: "bankRegistry", action: "E" },
              },
            ],
          },
          // Cash Book Management
          {
            path: "cashbook",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <CashBookDataView />,
                requiredPermission: { entityKey: "cashbook", action: "R" },
              },
              {
                path: "register",
                element: <CashBookRegister />,
                requiredPermission: { entityKey: "cashbook", action: "C" },
              },
              {
                path: "update",
                element: <CashBookUpdate />,
                requiredPermission: { entityKey: "cashbook", action: "E" },
              },
            ],
          },
          // Ledger Management
          {
            path: "ledger",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <LedgerDataView />,
                requiredPermission: { entityKey: "ledger", action: "R" },
              },
            ],
          },
          // Reports Management
          {
            path: "reports",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <ReportsDataView />,
                requiredPermission: { entityKey: "reports", action: "R" },
              },
            ],
          },
        ],
      },
      {
        path: "compliances",
        element: <Outlet />,
        allowedRoles: [],
        children: [
          {
            // Restored (was `<Navigate to="statutory" replace />` 2026-08-25 -> 2026-09-11):
            // product wants clicking "Compliances" in the sidebar to land on this 3-card
            // (Statutory/Legal/Annual) selector page instead of jumping straight into Statutory.
            index: true,
            element: <Compliances />,
          },
          // Statutory Compliances - Flat Routes
          {
            path: "statutory",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on the index child is the sole gate
            children: [
              {
                index: true,
                element: <StatutoryCompliances />,
                requiredPermission: { entityKey: "fpo", action: "R" },
              },
              // FPO/FPC Registration
              {
                path: "fpo_fpc_registration_view",
                element: <FpoFpcRegistrationDataView />,
                requiredPermission: { entityKey: "fpcCompliance", action: "R" },
              },
              {
                path: "fpo_fpc_registration_register",
                element: <FpoFpcRegistration />,
                requiredPermission: { entityKey: "fpcCompliance", action: "C" },
              },
              {
                path: "fpo_fpc_registration_register/:id",
                element: <FpoFpcRegistrationUpdate />,
                requiredPermission: { entityKey: "fpcCompliance", action: "E" },
              },
              // MSME Registration
              {
                path: "msme_registration_view",
                element: <MsmeRegistrationDataView />,
                requiredPermission: { entityKey: "msmeCompliance", action: "R" },
              },
              {
                path: "msme_registration_register",
                element: <MsmeRegistration />,
                requiredPermission: { entityKey: "msmeCompliance", action: "C" },
              },
              {
                path: "msme_registration_register/:id",
                element: <MsmeRegistrationUpdate />,
                requiredPermission: { entityKey: "msmeCompliance", action: "E" },
              },
              // Shops & Establishment
              {
                path: "shops_establishment_view",
                element: <ShopsEstablishmentRegistrationDataView />,
                requiredPermission: { entityKey: "shopCompliance", action: "R" },
              },
              {
                path: "shops_establishment_register",
                element: <ShopsEstablishmentRegistration />,
                requiredPermission: { entityKey: "shopCompliance", action: "C" },
              },
              {
                path: "shops_establishment_register/:id",
                element: <ShopsEstablishmentRegistrationUpdate />,
                requiredPermission: { entityKey: "shopCompliance", action: "E" },
              },
              // Company Registration
              {
                path: "company_view",
                element: <CompanyRegistrationDataView />,
                requiredPermission: { entityKey: "companyCompliance", action: "R" },
              },
              {
                path: "company_registration",
                element: <CompanyRegistration />,
                requiredPermission: { entityKey: "companyCompliance", action: "C" },
              },
              {
                path: "company_registration/:id",
                element: <CompanyRegistrationUpdate />,
                requiredPermission: { entityKey: "companyCompliance", action: "E" },
              },
              // PAN Registration
              {
                path: "pan_view",
                element: <PanRegistrationDataView />,
                requiredPermission: { entityKey: "panBusinessCompliance", action: "R" },
              },
              {
                path: "pan_registration",
                element: <PanRegistration />,
                requiredPermission: { entityKey: "panBusinessCompliance", action: "C" },
              },
              {
                path: "pan_registration/:id",
                element: <PanRegistrationUpdate />,
                requiredPermission: { entityKey: "panBusinessCompliance", action: "E" },
              },
              // Trade License
              {
                path: "trade_license_view",
                element: <TradeLicenseRegistrationDataView />,
                requiredPermission: { entityKey: "tradeCompliance", action: "R" },
              },
              {
                path: "trade_license_register",
                element: <TradeLicenseRegistration />,
                requiredPermission: { entityKey: "tradeCompliance", action: "C" },
              },
              {
                path: "trade_license_register/:id",
                element: <TradeLicenseRegistrationUpdate />,
                requiredPermission: { entityKey: "tradeCompliance", action: "E" },
              },
              // GST Registration
              {
                path: "gst_registration_view",
                element: <GstRegistrationDataView />,
                requiredPermission: { entityKey: "gstCompliance", action: "R" },
              },
              {
                path: "gst_registration_register",
                element: <GstRegistration />,
                requiredPermission: { entityKey: "gstCompliance", action: "C" },
              },
              {
                path: "gst_registration_register/:id",
                element: <GstRegistrationUpdate />,
                requiredPermission: { entityKey: "gstCompliance", action: "E" },
              },
              // FSSAI Registration
              {
                path: "fssai_registration_view",
                element: <FssaiRegistrationDataView />,
                requiredPermission: { entityKey: "fssaiCompliance", action: "R" },
              },
              {
                path: "fssai_registration_register",
                element: <FssaiRegistration />,
                requiredPermission: { entityKey: "fssaiCompliance", action: "C" },
              },
              {
                path: "fssai_registration_register/:id",
                element: <FssaiRegistrationUpdate />,
                requiredPermission: { entityKey: "fssaiCompliance", action: "E" },
              },
              // Labour License
              {
                path: "labour_license_view",
                element: <LabourLicenseRegistrationDataView />,
                requiredPermission: { entityKey: "labourCompliance", action: "R" },
              },
              {
                path: "labour_license_register",
                element: <LabourLicenseRegistration />,
                requiredPermission: { entityKey: "labourCompliance", action: "C" },
              },
              {
                path: "labour_license_register/:id",
                element: <LabourLicenseRegistrationUpdate />,
                requiredPermission: { entityKey: "labourCompliance", action: "E" },
              },
              // IEC Code Registration
              {
                path: "iec_code_view",
                element: <IecCodeRegistrationDataView />,
                requiredPermission: { entityKey: "iecCompliance", action: "R" },
              },
              {
                path: "iec_code_registration",
                element: <IecCodeRegistration />,
                requiredPermission: { entityKey: "iecCompliance", action: "C" },
              },
              {
                path: "iec_code_registration/:id",
                element: <IecCodeRegistrationUpdate />,
                requiredPermission: { entityKey: "iecCompliance", action: "E" },
              },
              // ISO Registration
              {
                path: "iso_view",
                element: <IsoRegistrationDataView />,
                requiredPermission: { entityKey: "isoCompliance", action: "R" },
              },
              {
                path: "iso_registration",
                element: <IsoRegistration />,
                requiredPermission: { entityKey: "isoCompliance", action: "C" },
              },
              {
                path: "iso_registration/:id",
                element: <IsoRegistrationUpdate />,
                requiredPermission: { entityKey: "isoCompliance", action: "E" },
              },
            ],
          },
          // Legal Compliances
          {
            path: "legal",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on the index child is the sole gate
            children: [
              {
                index: true,
                element: <LegalCompliances />,
                requiredPermission: { entityKey: "legalCompliance", action: "R" },
              },
            ],
          },
          // Annual Compliances
          {
            path: "annual",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on the index child is the sole gate
            children: [
              {
                index: true,
                element: <AnnualCompliances />,
                requiredPermission: { entityKey: "annualCompliance", action: "R" },
              },
            ],
          },
        ],
      },
      {
        path: "agm-board",
        element: <Outlet />,
        // Org-scoped: internal platform roles excluded, same as the module route above -- this is
        // the parent of the view/upload pages, so it must match or those stay reachable by URL.
        allowedRoles: ["fpoAdmin", "fpoMember"],
        children: [
          {
            index: true,
            element: <AgmBoardPage />,
          },
          // AGM Routes
          {
            path: "agm",
            element: <Outlet />,
            children: [
              // One page for every section's documents (AgmDocumentsPage.jsx); "*" keeps the old
              // per-section …_view / …_upload links working (the address picks the section).
              { index: true, element: <AgmDocumentsPage module="AGM" /> },
              { path: "*", element: <AgmDocumentsPage module="AGM" /> },
            ],
          },
          // Board of Directors Routes
          {
            path: "board",
            element: <Outlet />,
            children: [
              // One page for every section's documents (AgmDocumentsPage.jsx); "*" keeps the old
              // per-section …_view / …_upload links working (the address picks the section).
              { index: true, element: <AgmDocumentsPage module="BOD" /> },
              { path: "*", element: <AgmDocumentsPage module="BOD" /> },
            ],
          },
          // Rules & Acts - Government Links (Special Route)
          {
            path: "rules_acts",
            element: <RulesActsPage />,
          },
        ],
      },
    ],
  },

  // User Management
  {
    path: "/users",
    element: <Outlet />,
    serviceKey: "users",
    label: "User Management",
    img: "assets/img/category/users.webp",
    allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
    children: [
      {
        index: true,
        element: <AdminUsersDataTable />,
        requiredPermission: { entityKey: "users", action: "R" },
      },

      {
        path: "create",
        element: <AdminUsersPost />,
        requiredPermission: { entityKey: "users", action: "C" },
      },
      {
        path: "update",
        element: <AdminUsersUpdate />,
        requiredPermission: { entityKey: "users", action: "E" },
      },
      {
        path: ":id", // ✅ new route
        element: <AnonymoususerUpdate />, // component we'll create next
        requiredPermission: { entityKey: "users", action: "E" },
      },
    ],
  },

  // Other services
  {
    // isImplemented: false in ServicesConfig.js — ProtectedRoutes redirects to
    // /home/coming-soon before this route's element or the checks below ever run
    // (SERVICE_DASHBOARD_NAVIGATION_PLAN.md §4). Left in place, not dead code: these
    // become live again the moment isImplemented flips to true for this service.
    path: "/myfarm",
    element: <PlaceholderPage title="PRODUCT 360" />,
    serviceKey: "myfarm",
    allowedRoles: [], // B.5: cleared, requiredPermission is the sole gate
    requiredPermission: { entityKey: "myFarm", action: "R" },
  },
  {
    // isImplemented: false — see the /myfarm comment above; same Coming Soon short-circuit applies.
    path: "/myproducts",
    element: <PlaceholderPage title="MY PRODUCTS" />,
    serviceKey: "myproducts",
    allowedRoles: [], // B.5: cleared, requiredPermission is the sole gate
    requiredPermission: { entityKey: "products", action: "R" },
  },
  {
    path: "/mybusiness",
    element: (
      <ServiceProvider service={SERVICES.MY_BUSINESS}>
        <Outlet />
      </ServiceProvider>
    ),
    serviceKey: "mybusiness",
    label: "Business Services",
    img: "assets/img/category/business-contract.webp",
    allowedRoles: [],

    // fullUserData: SIDENAV_REDESIGN_PLAN.md Part 1, decision #3 — mirrors myFpo's getModules()
    // above, companyEntity instead of fpoEntity.
    getModules: (userRoles, hasEntityAccess, fullUserData) => {
      const hasPersonalEntity = !!(fullUserData?.companyEntity || fullUserData?.membershipEntity);
      // SIDENAV_REDESIGN_PLAN_V2.md §6 — mirrors myFpo's isInternalUser branch above exactly.
      const isInternalUser = fullUserData?.userTypeEntity?.internalUser === true;

      const allModules = [
        {
          path: "",
          label: "Dashboard",
          icon: <FaTachometerAlt />,
          category: "overview",
          allowedRoles: [],
        },
        // SIDENAV_REDESIGN_PLAN_V2.md §1 gap 7 — restored as its own top-level section, mirrors
        // myFpo's "About FPO" entry above.
        { path: "about", label: "About Business", icon: <FaTachometerAlt />, category: "about", allowedRoles: [] },
        ...(isInternalUser
          // SIDENAV_REDESIGN_PLAN_V2.md §6 — mirrors myFpo's revised internal branch above.
          ? [{
              path: "accounts",
              label: "My Account",
              icon: <FaFileInvoice />,
              category: "finance",
              requiredPermission: { entityKey: "company", action: "R" },
              children: [
                { path: "accounts/company", label: "Company Management", subGroup: "Registers", requiredPermission: { entityKey: "company", action: "R" } },
                { path: "accounts/products", label: "Product Management", subGroup: "Registers", requiredPermission: { entityKey: "businessProducts", action: "R" } },
              ],
            }]
          : !hasPersonalEntity
          ? [{
              path: "accounts",
              label: "My Account",
              icon: <FaFileInvoice />,
              category: "finance",
              requiredPermission: { entityKey: "company", action: "R" },
            }]
          : [{
              path: "accounts",
              label: "My Account",
              icon: <FaFileInvoice />,
              category: "finance",
              requiredPermission: { entityKey: "company", action: "R" },
              children: [
                { path: "accounts/vendor", label: "Vendor Management", subGroup: "Registers", requiredPermission: { entityKey: "businessVendor", action: "R" } },
                { path: "accounts/products", label: "Product Management", subGroup: "Registers", requiredPermission: { entityKey: "businessProducts", action: "R" } },
                { path: "accounts/purchase", label: "Purchase", subGroup: "Registers", requiredPermission: { entityKey: "businessPurchaseOrder", action: "R" } },
                { path: "accounts/sales", label: "Sales", subGroup: "Registers", requiredPermission: { entityKey: "businessSalesOrder", action: "R" } },
                { path: "accounts/stock", label: "Stock", subGroup: "Registers", requiredPermission: { entityKey: "businessStock", action: "R" } },
                { path: "accounts/share-capital", hash: "members", label: "Members", subGroup: "Members", requiredPermission: { entityKey: "businessShareCapital", action: "R" } },
                { path: "accounts/share-capital", hash: "share-transfer", label: "Share Transfer", subGroup: "Members", requiredPermission: { entityKey: "businessShareTransfer", action: "R" } },
                { path: "accounts/cashbook", label: "Cash", subGroup: "Transactions", requiredPermission: { entityKey: "businessCashbook", action: "R" } },
                { path: "accounts/bank", label: "Bank", subGroup: "Transactions", requiredPermission: { entityKey: "businessBankRegistry", action: "R" } },
                { path: "accounts/ledger", label: "Ledger", subGroup: "Transactions", requiredPermission: { entityKey: "businessLedger", action: "R" } },
                { path: "accounts/reports", label: "Reports", subGroup: "Transactions", requiredPermission: { entityKey: "businessReport", action: "R" } },
              ],
            }]),
        { path: "loans-schemas", label: "Loans & Schemes", icon: <FaMoneyCheckAlt />, category: "finance", requiredPermission: { entityKey: "company", action: "R" }, websitePath: "/know-your-schemes" }, // Know Your Schemes on the FEED WORLD website (new tab, signed in)

        // Real Coming Soon pages now, not a `path:""` + `#hash` anchor back into the Profile page
        // — mirrors myFpo's equivalent entries above.
        { path: "directory", label: "My Directory", icon: <FaUsers />, category: "directory", requiredPermission: { entityKey: "company", action: "R" } },
        { path: "trade", label: "My Trade", icon: <FaNetworkWired />, category: "trade", requiredPermission: { entityKey: "company", action: "R" } },

        {
          path: "compliances", label: "Compliances", icon: <FaClipboardCheck />, category: "governance",
          requiredPermission: { entityKey: "company", action: "R" },
          children: [
            { path: "compliances/statutory", label: "Statutory Compliances", requiredPermission: { entityKey: "company", action: "R" } },
            { path: "compliances/legal", label: "Legal Compliances", requiredPermission: { entityKey: "legalCompliance", action: "R" } },
            { path: "compliances/annual", label: "Annual Compliances", requiredPermission: { entityKey: "annualCompliance", action: "R" } },
          ],
        },
        { path: "agm-board", label: "Agm & Board", icon: <FaHandshake />, category: "governance", orgScoped: true, allowedRoles: ["businessAdmin", "businessMember"] },
        { path: "business-plan", label: "Business Plan", icon: <FaChartLine />, category: "growth", requiredPermission: { entityKey: "businessBusinessPlan", action: "R" } },
        { path: "capacity-building", label: "Capacity Building", icon: <FaUsers />, category: "growth", requiredPermission: { entityKey: "businessCapacityBuilding", action: "R" } },
        {
          // ROLE_TO_PERMISSION_MIGRATION_PLAN.md: migrated to requiredPermission, reusing the
          // same "businessReports" entity as My Account > Reports (explicit product decision).
          path: "reports",
          label: "Reports",
          icon: <FaFileAlt />,
          category: "growth",
          requiredPermission: { entityKey: "businessReports", action: "R" },
        },
      ];

      // Mirrors myFpo's getModules() annotate logic above exactly.
      // `orgScoped` modules opt out of systemAdmin's blanket unlock: their data belongs to an
      // individual organization (AGM & Board), and internal staff belong to none -- the backend
      // rejects them for the same reason. Every other module keeps the bypass as before.
      const computeAccessible = (module) => {
        if (userRoles?.systemAdmin) return !module.orgScoped;
        if (module.requiredPermission) {
          return !!hasEntityAccess?.(module.requiredPermission.entityKey, module.requiredPermission.action);
        }
        if (module.allowedRoles) {
          return module.allowedRoles.length === 0 || module.allowedRoles.some((role) => userRoles[role]);
        }
        return true;
      };
      const annotate = (module) => {
        if (module.children) {
          const children = module.children.map(annotate);
          const anyChildUnlocked = children.some((c) => c.path && !c.locked);
          const ownAccessible = module.requiredPermission ? computeAccessible(module) : false;
          return { ...module, children, locked: !(anyChildUnlocked || ownAccessible) };
        }
        return { ...module, locked: !computeAccessible(module) };
      };
      return allModules.map(annotate);
    },

    children: [
      {
        index: true,
        element: <MyBusinessDashboard />,
      },
      {
        path: "about",
        element: <AboutBusinessPage />,
        allowedRoles: [],
      },
      {
        // B.5-style: allowedRoles swapped for the coarse "company" entity check, matching the
        // SideNav's own gate for this module — SIDENAV_REDESIGN_PLAN.md Part 1.
        path: "business-plan",
        element: <BusinessPlanPage />,
        allowedRoles: [],
        requiredPermission: { entityKey: "businessBusinessPlan", action: "R" },
      },
      {
        // No backend/permission concept, same as ERS — every account gets this page, matching
        // KrishiCoinsWidget.jsx's own Dashboard card (no locked/no-access state either).
        path: "krishi-coins",
        element: <KrishiCoinsPage />,
        allowedRoles: [],
      },
      {
        // PlanDetailsWidget.jsx's "Manage Plan" — same SubscriptionPage.jsx component already
        // registered at /home/subscription (self-contained: reads ?service= itself, no
        // dependency on being under Home.jsx specifically), registered here too so it renders
        // inside this service's own ServicesLayout/SideNav shell instead of jumping out to the
        // global Home one. No backend/permission concept, same as krishi-coins above.
        path: "subscription",
        element: <SubscriptionPage />,
        allowedRoles: [],
      },
      {
        // Mirrors /home/profile — same self-contained ProfilePage.jsx (reads AuthContext only,
        // no Home.jsx dependency), registered here too so the Service SideNav's persistent
        // "My Profile" row opens it inside this service's own ServicesLayout/SideNav shell
        // instead of jumping out to the global Home layout. No backend/permission concept.
        path: "profile",
        label: "My Profile",
        element: <ProfilePage />,
        allowedRoles: [],
      },
      {
        path: "capacity-building",
        element: <CapacityBuildingPage />,
        allowedRoles: [],
        requiredPermission: { entityKey: "businessCapacityBuilding", action: "R" },
      },
      {
        path: "agm-board",
        // Same org-scoping as the MyFPO route above -- internal platform roles excluded.
        allowedRoles: ["fpoAdmin", "fpoMember", "businessAdmin", "businessMember"],
        element: <AgmBoardPage />,
      },
      {
        path: "loans-schemas",
        element: <LoansSchemasPage />,
        allowedRoles: [],
        requiredPermission: { entityKey: "company", action: "R" },
      },
      {
        path: "directory",
        element: <MyDirectoryPage />,
        allowedRoles: [],
        requiredPermission: { entityKey: "company", action: "R" },
      },
      {
        path: "trade",
        element: <MyTradePage />,
        allowedRoles: [],
        requiredPermission: { entityKey: "company", action: "R" },
      },
      {
        path: "reports",
        element: <ReportsPage />,
        requiredPermission: { entityKey: "businessReports", action: "R" },
      },
      {
        // Service-mode's own "Services" link (SideNav.jsx) points here instead of the top-level
        // /services — see the matching myFpo comment above.
        path: "services",
        element: <ServicesPage />,
        allowedRoles: [],
      },

      // Company entity setup - shown when a business user has no companyEntity yet, AND (see
      // ProtectedRoutes.jsx's isEntitySetupPath exception) reachable by a user who doesn't hold
      // businessAdmin yet at all - e.g. an Institutional account redirected here for a
      // cross-usertype subscribe. The role is only ever assigned at approval time, after this
      // form is submitted, so requiring it up front would make this page unreachable for exactly
      // the account it exists for.
      {
        path: "setup",
        element: <CompanySetupPage />,
        allowedRoles: [],
      },

      // Accounts with three-level nesting (shared modules reuse identical MyFPO pages)
      {
        path: "accounts",
        element: <Outlet />,
        allowedRoles: [], // B.5: cleared, requiredPermission on the index child is the sole gate
        children: [
          {
            index: true,
            element: <FpoAccountHomePage />,
            requiredPermission: { entityKey: "company", action: "R" },
          },
          // Company Management (replaces FPC Management) — internal staff only (see
          // AskUserQuestion 2026-08-24)
          {
            path: "company",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            requiresInternalUser: true,
            children: [
              {
                index: true,
                element: <CompanyDataView />,
                requiredPermission: { entityKey: "company", action: "R" },
              },
              {
                path: "register",
                element: <CompanyRegister />,
                label: "Register Company",
                requiredPermission: { entityKey: "company", action: "C" },
              },
              {
                path: "view",
                element: <CompanyUpdate />,
                requiredPermission: { entityKey: "company", action: "R" },
              },
              {
                path: "update",
                element: <CompanyUpdate />,
                requiredPermission: { entityKey: "company", action: "E" },
              },
            ],
          },
          // Membership Management (member CRUD only — Share Capital/Transfer stay in their own card, shared)
          {
            path: "membership",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <MemberDataView />,
                requiredPermission: { entityKey: "membership", action: "R" },
              },
              {
                path: "register",
                element: <MemberPost />,
                requiredPermission: { entityKey: "membership", action: "C" },
              },
              {
                path: "view",
                element: <MemberUpdate />,
                requiredPermission: { entityKey: "membership", action: "R" },
              },
              {
                path: "update",
                element: <MemberUpdate />,
                requiredPermission: { entityKey: "membership", action: "E" },
              },
            ],
          },
          // Vendor Management
          {
            path: "vendor",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <VendorDataView />,
                requiredPermission: { entityKey: "businessVendor", action: "R" },
              },
              {
                path: "register",
                element: <VendorRegister />,
                requiredPermission: { entityKey: "businessVendor", action: "C" },
              },
              {
                path: "update",
                element: <VendorUpdate />,
                requiredPermission: { entityKey: "businessVendor", action: "E" },
              },
            ],
          },
          // Product Management
          {
            path: "products",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <ProductDataView />,
                requiredPermission: { entityKey: "businessProducts", action: "R" },
              },
              {
                path: "register",
                element: <ProductRegister />,
                requiredPermission: { entityKey: "businessProducts", action: "C" },
              },
              {
                path: ":id",
                element: <ProductUpdate />,
                requiredPermission: { entityKey: "businessProducts", action: "E" },
              },
              {
                path: "addOwnProduct",
                element: <FpoOwnProductRegister />,
                requiredPermission: { entityKey: "businessProducts", action: "C" },
              },
              {
                path: "viewOwnProducts",
                element: <FpoOwnProductView />,
                requiredPermission: { entityKey: "businessProducts", action: "R" },
              },
            ],
          },
          // Share Capital Management
          {
            path: "share-capital",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <ShareCapitalDataView />,
                requiredPermission: { entityKey: "businessShareCapital", action: "R" },
              },
              {
                path: "register",
                element: <ShareCapitalRegister />,
                requiredPermission: { entityKey: "businessShareCapital", action: "C" },
              },
              {
                path: "update/:id/:type",
                element: <ShareCapitalUpdate />,
                // "E" relaxed to "R": the shareCapital type renders read-only for view-only
                // users (see ShareCapitalUpdate.jsx); member/shareTransfer still self-enforce
                // Edit via their own in-component canEditForType check.
                requiredPermission: { entityKey: "businessShareCapital", action: "R" },
              },
            ],
          },
          // Purchase Management
          {
            path: "purchase",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <PurchaseDataView />,
                requiredPermission: { entityKey: "businessPurchaseOrder", action: "R" },
              },
              {
                path: "register",
                element: <PurchaseRegister />,
                requiredPermission: { entityKey: "businessPurchaseOrder", action: "C" },
              },
              {
                path: "update",
                element: <PurchaseUpdateWrapper />,
                requiredPermission: { entityKey: "businessPurchaseOrder", action: "E" },
              },
            ],
          },
          // Sales Management
          {
            path: "sales",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <SalesDataView />,
                requiredPermission: { entityKey: "businessSalesOrder", action: "R" },
              },
              {
                path: "register",
                element: <SalesRegister />,
                requiredPermission: { entityKey: "businessSalesOrder", action: "C" },
              },
              {
                path: "update",
                element: <SalesUpdateWrapper />,
                requiredPermission: { entityKey: "businessSalesOrder", action: "E" },
              },
            ],
          },
          {
            path: "stock",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <StockDataView />,
                requiredPermission: { entityKey: "businessStock", action: "R" },
              },
            ],
          },
          // Bank Management
          {
            path: "bank",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <BankDataView />,
                requiredPermission: { entityKey: "businessBankRegistry", action: "R" },
              },
              {
                path: "register",
                element: <BankRegister />,
                requiredPermission: { entityKey: "businessBankRegistry", action: "C" },
              },
              {
                path: "update",
                element: <BankUpdate />,
                requiredPermission: { entityKey: "businessBankRegistry", action: "E" },
              },
            ],
          },
          // Cash Book Management
          {
            path: "cashbook",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <CashBookDataView />,
                requiredPermission: { entityKey: "businessCashbook", action: "R" },
              },
              {
                path: "register",
                element: <CashBookRegister />,
                requiredPermission: { entityKey: "businessCashbook", action: "C" },
              },
              {
                path: "update",
                element: <CashBookUpdate />,
                requiredPermission: { entityKey: "businessCashbook", action: "E" },
              },
            ],
          },
          // Ledger Management
          {
            path: "ledger",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <LedgerDataView />,
                requiredPermission: { entityKey: "businessLedger", action: "R" },
              },
            ],
          },
          // Reports Management
          {
            path: "reports",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
            children: [
              {
                index: true,
                element: <ReportsDataView />,
                requiredPermission: { entityKey: "businessReports", action: "R" },
              },
            ],
          },
        ],
      },
      {
        path: "compliances",
        element: <Outlet />,
        allowedRoles: [],
        children: [
          {
            // Restored (was `<Navigate to="statutory" replace />` 2026-08-25 -> 2026-09-11):
            // product wants clicking "Compliances" in the sidebar to land on this 3-card
            // (Statutory/Legal/Annual) selector page instead of jumping straight into Statutory.
            index: true,
            element: <Compliances />,
          },
          // Statutory Compliances - Flat Routes
          {
            path: "statutory",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on the index child is the sole gate
            children: [
              {
                index: true,
                element: <StatutoryCompliances />,
                requiredPermission: { entityKey: "company", action: "R" },
              },
              // FPO/FPC Registration
              {
                path: "fpo_fpc_registration_view",
                element: <FpoFpcRegistrationDataView />,
                requiredPermission: { entityKey: "fpcCompliance", action: "R" },
              },
              {
                path: "fpo_fpc_registration_register",
                element: <FpoFpcRegistration />,
                requiredPermission: { entityKey: "fpcCompliance", action: "C" },
              },
              {
                path: "fpo_fpc_registration_register/:id",
                element: <FpoFpcRegistrationUpdate />,
                requiredPermission: { entityKey: "fpcCompliance", action: "E" },
              },
              // MSME Registration
              {
                path: "msme_registration_view",
                element: <MsmeRegistrationDataView />,
                requiredPermission: { entityKey: "msmeCompliance", action: "R" },
              },
              {
                path: "msme_registration_register",
                element: <MsmeRegistration />,
                requiredPermission: { entityKey: "msmeCompliance", action: "C" },
              },
              {
                path: "msme_registration_register/:id",
                element: <MsmeRegistrationUpdate />,
                requiredPermission: { entityKey: "msmeCompliance", action: "E" },
              },
              // Shops & Establishment
              {
                path: "shops_establishment_view",
                element: <ShopsEstablishmentRegistrationDataView />,
                requiredPermission: { entityKey: "shopCompliance", action: "R" },
              },
              {
                path: "shops_establishment_register",
                element: <ShopsEstablishmentRegistration />,
                requiredPermission: { entityKey: "shopCompliance", action: "C" },
              },
              {
                path: "shops_establishment_register/:id",
                element: <ShopsEstablishmentRegistrationUpdate />,
                requiredPermission: { entityKey: "shopCompliance", action: "E" },
              },
              // Company Registration
              {
                path: "company_view",
                element: <CompanyRegistrationDataView />,
                requiredPermission: { entityKey: "companyCompliance", action: "R" },
              },
              {
                path: "company_registration",
                element: <CompanyRegistration />,
                requiredPermission: { entityKey: "companyCompliance", action: "C" },
              },
              {
                path: "company_registration/:id",
                element: <CompanyRegistrationUpdate />,
                requiredPermission: { entityKey: "companyCompliance", action: "E" },
              },
              // PAN Registration
              {
                path: "pan_view",
                element: <PanRegistrationDataView />,
                requiredPermission: { entityKey: "panBusinessCompliance", action: "R" },
              },
              {
                path: "pan_registration",
                element: <PanRegistration />,
                requiredPermission: { entityKey: "panBusinessCompliance", action: "C" },
              },
              {
                path: "pan_registration/:id",
                element: <PanRegistrationUpdate />,
                requiredPermission: { entityKey: "panBusinessCompliance", action: "E" },
              },
              // Trade License
              {
                path: "trade_license_view",
                element: <TradeLicenseRegistrationDataView />,
                requiredPermission: { entityKey: "tradeCompliance", action: "R" },
              },
              {
                path: "trade_license_register",
                element: <TradeLicenseRegistration />,
                requiredPermission: { entityKey: "tradeCompliance", action: "C" },
              },
              {
                path: "trade_license_register/:id",
                element: <TradeLicenseRegistrationUpdate />,
                requiredPermission: { entityKey: "tradeCompliance", action: "E" },
              },
              // GST Registration
              {
                path: "gst_registration_view",
                element: <GstRegistrationDataView />,
                requiredPermission: { entityKey: "gstCompliance", action: "R" },
              },
              {
                path: "gst_registration_register",
                element: <GstRegistration />,
                requiredPermission: { entityKey: "gstCompliance", action: "C" },
              },
              {
                path: "gst_registration_register/:id",
                element: <GstRegistrationUpdate />,
                requiredPermission: { entityKey: "gstCompliance", action: "E" },
              },
              // FSSAI Registration
              {
                path: "fssai_registration_view",
                element: <FssaiRegistrationDataView />,
                requiredPermission: { entityKey: "fssaiCompliance", action: "R" },
              },
              {
                path: "fssai_registration_register",
                element: <FssaiRegistration />,
                requiredPermission: { entityKey: "fssaiCompliance", action: "C" },
              },
              {
                path: "fssai_registration_register/:id",
                element: <FssaiRegistrationUpdate />,
                requiredPermission: { entityKey: "fssaiCompliance", action: "E" },
              },
              // Labour License
              {
                path: "labour_license_view",
                element: <LabourLicenseRegistrationDataView />,
                requiredPermission: { entityKey: "labourCompliance", action: "R" },
              },
              {
                path: "labour_license_register",
                element: <LabourLicenseRegistration />,
                requiredPermission: { entityKey: "labourCompliance", action: "C" },
              },
              {
                path: "labour_license_register/:id",
                element: <LabourLicenseRegistrationUpdate />,
                requiredPermission: { entityKey: "labourCompliance", action: "E" },
              },
              // IEC Code Registration
              {
                path: "iec_code_view",
                element: <IecCodeRegistrationDataView />,
                requiredPermission: { entityKey: "iecCompliance", action: "R" },
              },
              {
                path: "iec_code_registration",
                element: <IecCodeRegistration />,
                requiredPermission: { entityKey: "iecCompliance", action: "C" },
              },
              {
                path: "iec_code_registration/:id",
                element: <IecCodeRegistrationUpdate />,
                requiredPermission: { entityKey: "iecCompliance", action: "E" },
              },
              // ISO Registration
              {
                path: "iso_view",
                element: <IsoRegistrationDataView />,
                requiredPermission: { entityKey: "isoCompliance", action: "R" },
              },
              {
                path: "iso_registration",
                element: <IsoRegistration />,
                requiredPermission: { entityKey: "isoCompliance", action: "C" },
              },
              {
                path: "iso_registration/:id",
                element: <IsoRegistrationUpdate />,
                requiredPermission: { entityKey: "isoCompliance", action: "E" },
              },
            ],
          },
          // Legal Compliances
          {
            path: "legal",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on the index child is the sole gate
            children: [
              {
                index: true,
                element: <LegalCompliances />,
                requiredPermission: { entityKey: "legalCompliance", action: "R" },
              },
            ],
          },
          // Annual Compliances
          {
            path: "annual",
            element: <Outlet />,
            allowedRoles: [], // B.5: cleared, requiredPermission on the index child is the sole gate
            children: [
              {
                index: true,
                element: <AnnualCompliances />,
                requiredPermission: { entityKey: "annualCompliance", action: "R" },
              },
            ],
          },
        ],
      },
      {
        path: "agm-board",
        element: <Outlet />,
        // Org-scoped: internal platform roles excluded -- parent of the view/upload pages.
        allowedRoles: ["fpoAdmin", "fpoMember", "businessAdmin", "businessMember"],
        children: [
          {
            index: true,
            element: <AgmBoardPage />,
          },
          // AGM Routes
          {
            path: "agm",
            element: <Outlet />,
            children: [
              // One page for every section's documents (AgmDocumentsPage.jsx); "*" keeps the old
              // per-section …_view / …_upload links working (the address picks the section).
              { index: true, element: <AgmDocumentsPage module="AGM" /> },
              { path: "*", element: <AgmDocumentsPage module="AGM" /> },
            ],
          },
          // Board of Directors Routes
          {
            path: "board",
            element: <Outlet />,
            children: [
              // One page for every section's documents (AgmDocumentsPage.jsx); "*" keeps the old
              // per-section …_view / …_upload links working (the address picks the section).
              { index: true, element: <AgmDocumentsPage module="BOD" /> },
              { path: "*", element: <AgmDocumentsPage module="BOD" /> },
            ],
          },
          // Rules & Acts - Government Links (Special Route)
          {
            path: "rules_acts",
            element: <RulesActsPage />,
          },
        ],
      },
    ],
  },
  {
    // isImplemented: false — see the /myfarm comment above; same Coming Soon short-circuit applies.
    path: "/myexports",
    element: <PlaceholderPage title="MY EXPORTS" />,
    serviceKey: "myexports",
    allowedRoles: [], // B.5: cleared, requiredPermission is the sole gate
    requiredPermission: { entityKey: "myExports", action: "R" },
  },
  {
    // isImplemented: false — see the /myfarm comment above; same Coming Soon short-circuit applies.
    // B.4 (PERMISSIONS_IMPLEMENTATION_PLAN.md) — migrated off the Phase A dynamicRoleMembership
    // pilot onto requiredPermission, consistent with every other business route. Confirmed
    // parity before migrating: education_admin/education_department/education_viewer all hold
    // EDUCATION.read() (SystemRolePermissions.java), the same real-world access the old
    // ServiceRoleMappingEntity-driven check granted — this is not a behavior change, just a
    // simpler mechanism (no live network call, reuses the already-loaded login payload).
    path: "/myeducation",
    element: <PlaceholderPage title="MY EDUCATION" />,
    serviceKey: "myeducation",
    requiredPermission: { entityKey: "myEducation", action: "R" },
  },
  {
    // isImplemented: false — see the /myfarm comment above; same Coming Soon short-circuit applies.
    path: "/mymarket",
    element: <PlaceholderPage title="MY MARKET" />,
    serviceKey: "mymarket",
    allowedRoles: [], // B.5: cleared, requiredPermission is the sole gate
    requiredPermission: { entityKey: "myMarket", action: "R" },
  },
  {
    // isImplemented: false — see the /myfarm comment above; same Coming Soon short-circuit applies.
    path: "/mytools",
    element: <PlaceholderPage title="MY TOOLS" />,
    serviceKey: "mytools",
    allowedRoles: [], // B.5: cleared, requiredPermission is the sole gate
    requiredPermission: { entityKey: "myTools", action: "R" },
  },

  // Admin — User Types & Subscription Plans (systemAdmin only)
  {
    path: "/admin",
    element: <Outlet />,
    serviceKey: "admin",
    label: "Admin Settings",
    img: "assets/img/category/users.webp",
    allowedRoles: [], // B.5: cleared, requiredPermission on every child is the sole gate
    modules: [
      {
        path: "user-types",
        label: "User Types",
        icon: <FaShieldAlt />,
      },
      {
        path: "subscription-plans",
        label: "Subscription Plans",
        icon: <FaMoneyCheckAlt />,
      },
      {
        path: "roles",
        label: "Roles",
        icon: <FaUsers />,
      },
      {
        path: "yuva-krushi",
        label: "Yuva Krushi",
        icon: <FaSeedling />,
      },
      {
        path: "safe-mission",
        label: "Safe Mission",
        icon: <FaGlobeAsia />,
      },
      {
        path: "ers-assessments",
        label: "ERS Assessments",
        icon: <FaClipboardCheck />,
      },
      {
        path: "agm-documents",
        label: "AGM Documents",
        icon: <FaFolderOpen />,
      },
      {
        path: "news-alerts",
        label: "News & Alerts",
        icon: <FaBullhorn />,
      },
      {
        path: "directory-suggestions",
        label: "Directory Suggestions",
        icon: <FaAddressBook />,
      },
      {
        path: "government-officers",
        label: "Government Officers",
        icon: <FaLandmark />,
      },
      {
        path: "progress-report-data",
        label: "Progress Report Data",
        icon: <FaFileUpload />,
      },
      {
        path: "payments",
        label: "Payments",
        icon: <FaReceipt />,
      },
      {
        path: "payment-settings",
        label: "Membership & Payment Settings",
        icon: <FaRupeeSign />,
      },
    ],
    children: [
      {
        index: true,
        element: <AccessibilityRulePage />,
        requiredPermission: { entityKey: "roles", action: "R" },
      },
      {
        path: "user-types",
        element: <AccessibilityRulePage />,
        requiredPermission: { entityKey: "roles", action: "R" },
      },
      {
        path: "subscription-plans",
        element: <SubscriptionPlanPage />,
        requiredPermission: { entityKey: "roles", action: "R" },
      },
      {
        // "Join Yuva Krushi" registrations from the FEED WORLD website (fpo API: System Administrators only)
        path: "yuva-krushi",
        element: <YuvaKrushiRegistrationsPage />,
        requiredPermission: { entityKey: "roles", action: "R" },
      },
      {
        // "Join S.A.F.E. Mission" registrations from the FEED website (fpo API: System Administrators only)
        path: "safe-mission",
        element: <SafeMissionRegistrationsPage />,
        requiredPermission: { entityKey: "roles", action: "R" },
      },
      {
        // Export Readiness Score attempts from the FEED WORLD website (fpo API: System Administrators only)
        path: "ers-assessments",
        element: <ErsAssessmentsPage />,
        requiredPermission: { entityKey: "roles", action: "R" },
      },
      {
        // Every organisation's AGM & Board documents; delete one for good (fpo API: System Administrators only)
        path: "agm-documents",
        element: <AgmDocumentsAdminPage />,
      },
      {
        // General notifications and customized FPO reminders (fpo API: System Administrators only)
        path: "news-alerts",
        element: <NewsAlertsAdminPage />,
        requiredPermission: { entityKey: "roles", action: "R" },
      },
      {
        // "Post Your Suggestion" from the website's My Directory (fpo API: System Administrators only)
        path: "directory-suggestions",
        element: <DirectorySuggestionsPage />,
        requiredPermission: { entityKey: "roles", action: "R" },
      },
      {
        // Government users' office area for the Progress Report (fpo API: System Administrators only)
        path: "government-officers",
        element: <GovernmentOfficersPage />,
        requiredPermission: { entityKey: "roles", action: "R" },
      },
      {
        // The Progress Report's monthly figures from Excel: template, upload, check, publish (fpo API: System Administrators only)
        path: "progress-report-data",
        element: <ProgressReportDataPage />,
        requiredPermission: { entityKey: "roles", action: "R" },
      },
      {
        // Razorpay payments: membership fees and subscriptions (fpo API: System Administrators only)
        path: "payments",
        element: <PaymentsAdminPage />,
        requiredPermission: { entityKey: "roles", action: "R" },
      },
      {
        // Membership fee per account type, GST and receipt details (fpo API: System Administrators only)
        path: "payment-settings",
        element: <PaymentSettingsPage />,
        requiredPermission: { entityKey: "roles", action: "R" },
      },
      {
        path: "service-subscription-pricing",
        element: <ServiceSubscriptionConfigPage />,
        requiredPermission: { entityKey: "roles", action: "R" },
      },
      {
        path: "roles",
        element: <RolePage />,
        requiredPermission: { entityKey: "roles", action: "R" },
      },
    ],
  },
];
